import React from "react";
import { Csc } from "@cimo/schedule_calendar/dist/src/Main";

// Source
import StoreRoute from "../../store/Route";
import StoreRole from "../../store/Role";
import StoreLocalization from "../../store/Localization";
import StorePreference from "../../store/Preference";
import * as HelperSrc from "../../HelperSrc";
import * as ModelRoute from "../../model/Route";
import * as ModelCustomRoute from "../../model/custom/Route";
import * as ModelRole from "../../model/Role";
import * as ModelLocalization from "../../model/Localization";
import * as ModelCustomLocalization from "../../model/custom/Localization";
import * as ModelPreference from "../../model/Preference";
import * as ApiTask from "../../api/custom/Task";
import * as ModelTask from "../../model/custom/Task";
import ControllerAlert from "../Alert";

export default class ControllerTaskList {
    // Variable
    private storeRoute: ModelRoute.Istore<ModelCustomRoute.Idata>;
    private storageRouteData = React.useRef<ModelCustomRoute.Idata | undefined>(undefined);
    private storeRole: ModelRole.Istore;
    private storageRoleData = React.useRef<ModelRole.Idata | undefined>(undefined);
    private storeLocalization: ModelLocalization.Istore<ModelCustomLocalization.Idata>;
    private storageLocalizationData = React.useRef<ModelCustomLocalization.Idata | undefined>(undefined);
    private storePreference: ModelPreference.Istore<ModelPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelPreference.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementMonth = React.useRef<HTMLInputElement | null>(null);
    private elementViewMain = React.useRef<HTMLElement | null>(null);

    private isRendered = React.useRef<boolean>(false);
    private dataMainList = React.useRef<ModelTask.Idata[] | undefined>(undefined);
    private dataMain = React.useRef<ModelTask.Idata | undefined>(undefined);
    private taskDay = React.useRef<number>(0);
    private weekdayLabelList = React.useRef<string[] | undefined>(undefined);
    private year = React.useRef<number>(0);
    private month = React.useRef<number>(0);
    private locale = React.useRef<string>("");
    private taskLabel = React.useRef<string>("");

    private csc: Csc;

    getStorageRole: ModelRole.Idata | undefined;

    getApiLoadingList: Record<string, boolean>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    getDataMain: ModelTask.Idata | undefined;
    setDataMain: React.Dispatch<React.SetStateAction<ModelTask.Idata | undefined>>;

    getTaskDay: number;
    setTaskDay: React.Dispatch<number>;

    getIsTaskDetailDialogOpen: boolean;
    setIsTaskDetailDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

    getIsTaskListDialogOpen: boolean;
    setIsTaskListDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

    getSelectOption: ModelTask.IselectOption | undefined;
    setSelectOption: React.Dispatch<React.SetStateAction<ModelTask.IselectOption | undefined>>;

    getDataMainList: ModelTask.Idata[] | undefined;
    setDataMainList: React.Dispatch<React.SetStateAction<ModelTask.Idata[] | undefined>>;

    // Method
    constructor(isRendered: boolean) {
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();
        this.storeRole = new StoreRole();
        this.storageRoleData.current = this.storeRole.storageGet();
        this.storeLocalization = new StoreLocalization();
        this.storageLocalizationData.current = this.storeLocalization.storageGet();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.csc = new Csc({ yearBack: 10, yearForward: 10, isStartOnMonday: false, isHighlightToday: true }, ".view_task_calendar");

        this.csc.callbackCurrent = (year: number, month: number) => {
            this.monthlyTaskList(year, month);
        };

        this.csc.callbackCell = (elementDiv: HTMLDivElement, day: number) => {
            this.dailyTaskList(elementDiv, day);
        };

        this.isRendered.current = isRendered;

        const [getStorageRole] = React.useState<ModelRole.Idata | undefined>(this.storageRoleData.current);
        this.getStorageRole = getStorageRole;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getDataMain, setDataMain] = React.useState<ModelTask.Idata | undefined>(this.dataMain.current);
        this.getDataMain = getDataMain;
        this.setDataMain = setDataMain;

        const [getTaskDay, setTaskDay] = React.useState<number>(this.taskDay.current);
        this.getTaskDay = getTaskDay;
        this.setTaskDay = setTaskDay;

        const [getIsTaskDetailDialogOpen, setIsTaskDetailDialogOpen] = React.useState(false);
        this.getIsTaskDetailDialogOpen = getIsTaskDetailDialogOpen;
        this.setIsTaskDetailDialogOpen = setIsTaskDetailDialogOpen;

        const [getIsTaskListDialogOpen, setIsTaskListDialogOpen] = React.useState(false);
        this.getIsTaskListDialogOpen = getIsTaskListDialogOpen;
        this.setIsTaskListDialogOpen = setIsTaskListDialogOpen;

        const [getSelectOption, setSelectOption] = React.useState<ModelTask.IselectOption | undefined>(undefined);
        this.getSelectOption = getSelectOption;
        this.setSelectOption = setSelectOption;

        const [getDataMainList, setDataMainList] = React.useState<ModelTask.Idata[] | undefined>(this.dataMainList.current);
        this.getDataMainList = getDataMainList;
        this.setDataMainList = setDataMainList;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_task_calendar", () => {
                this.initializeHtmlElement();
            });

            this.apiSelectOption();
        }, []);

        React.useEffect(() => {
            this.storageRouteData.current = this.storeRoute.storageGet();
            this.storageRoleData.current = this.storeRole.storageGet();
            this.storageLocalizationData.current = this.storeLocalization.storageGet();
            this.storagePreferenceData.current = this.storePreference.storageGet();

            if (this.storagePreferenceData.current && this.storageLocalizationData.current) {
                const initialTaskLabel = this.taskLabel.current;

                this.taskLabel.current = this.storageLocalizationData.current.task;
                this.locale.current = `${this.storagePreferenceData.current.language}-${this.storagePreferenceData.current.country.toUpperCase()}`;

                this.weekdayLabelList.current = [
                    this.storageLocalizationData.current.sunday,
                    this.storageLocalizationData.current.monday,
                    this.storageLocalizationData.current.tuesday,
                    this.storageLocalizationData.current.wednesday,
                    this.storageLocalizationData.current.thursday,
                    this.storageLocalizationData.current.friday,
                    this.storageLocalizationData.current.saturday
                ];

                if (this.taskLabel.current !== initialTaskLabel) {
                    this.csc.setLocale(this.locale.current);
                    this.csc.setWeekdayList(this.weekdayLabelList.current);
                    this.csc.view();
                }
            }
        }, [this.storageRouteData.current, this.storageRoleData.current, this.storageLocalizationData.current, this.storagePreferenceData.current]);
    };

    clickTaskDetailDialogClose = (): void => {
        this.setDataMain(undefined);

        this.setIsTaskDetailDialogOpen(false);
    };

    clickTaskListDialogClose = (): void => {
        this.setTaskDay(0);

        this.setIsTaskListDialogOpen(false);
    };

    clickItemDetail = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, data: ModelTask.Idata | undefined): void => {
        const element = event.target as HTMLElement;
        const tagName = element.tagName;

        if (data && this.storageRouteData.current && tagName !== "path") {
            HelperSrc.routerHistoryPush(this.storageRouteData.current.task_profile.path, data as unknown as Record<string, unknown>);
        }
    };

    clickTask = (data: ModelTask.Idata): void => {
        this.setTaskDay(0);
        this.setDataMain(data);

        this.setIsTaskListDialogOpen(false);
        this.setIsTaskDetailDialogOpen(true);
    };

    private apiSelectOption = (): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            selectOption: true
        }));

        ApiTask.selectOption((response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                selectOption: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.setSelectOption(response.data.result as unknown as ModelTask.IselectOption);
        });
    };

    private clickTaskButton = (day: number): void => {
        this.setTaskDay(day);

        this.setIsTaskListDialogOpen(true);
    };

    private taskButton = (elementDiv: HTMLDivElement, day: number): void => {
        if (this.storageLocalizationData.current) {
            const buttonElement = document.createElement("button");
            buttonElement.textContent = `${this.storageLocalizationData.current.task}`;
            buttonElement.classList.add("task_button");
            buttonElement.classList.add("button_primary");
            buttonElement.onclick = () => {
                this.clickTaskButton(day);
            };

            elementDiv.appendChild(buttonElement);
        }
    };

    private dailyTaskList = (elementDiv: HTMLDivElement, day: number): void => {
        if (this.dataMainList.current) {
            this.taskButton(elementDiv, day);

            for (const task of this.dataMainList.current) {
                const taskDate = new Date(task.day);

                if (this.year.current == taskDate.getFullYear() && this.month.current == taskDate.getMonth() && day == taskDate.getDate()) {
                    const taskElement = document.createElement("div");
                    taskElement.textContent = `${task.id}: ${task.name}`;
                    taskElement.classList.add("daily_task");
                    taskElement.setAttribute("data-at_id", `calendar_detail_${task.name}`);
                    taskElement.onclick = () => {
                        this.clickTask(task);
                    };

                    elementDiv.classList.add("has_task");
                    elementDiv.appendChild(taskElement);
                }
            }
        }
    };

    private monthlyTaskList = (year: number, month: number): void => {
        const initialYear = this.year.current;
        const initialMonth = this.month.current;

        if (initialYear !== year || initialMonth !== month) {
            this.year.current = year;
            this.month.current = month;

            const monthFormatted = String(month + 1).padStart(2, "0");
            const monthFilter = `${year}/${monthFormatted}`;

            this.setApiLoadingList((prev) => ({
                ...prev,
                dataList: true
            }));

            ApiTask.taskList(monthFilter, (response) => {
                this.setApiLoadingList((prev) => ({
                    ...prev,
                    dataList: false
                }));

                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    return;
                }

                this.dataMainList.current = response.data.result as unknown as ModelTask.Idata[];
                this.setDataMainList(this.dataMainList.current);

                this.csc.view();
            });
        }
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementMonth.current = document.querySelector<HTMLInputElement>("[name='month']");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_task_calendar");

            const date = new Date();
            this.monthlyTaskList(date.getFullYear(), date.getMonth());
        }
    };
}
